import { SurveyData, Statistics } from '../types/data';

// Centralized sorting utility for consistent ordering across all visualizations
export const sortCategories = (categories: string[], demographic: string): string[] => {
  if (demographic === 'edad') {
    return categories.sort((a, b) => {
      const ageOrder = { '30-39': 1, '40-49': 2, '50-59': 3, '60 o más': 4 };
      return (ageOrder[a as keyof typeof ageOrder] || 0) - (ageOrder[b as keyof typeof ageOrder] || 0);
    });
  }
  
  if (demographic === 'experiencia_pastoral') {
    return categories.sort((a, b) => {
      const expOrder = { 'Menos de 5 años': 1, '5-10 años': 2, '11-20 años': 3, 'Más de 20 años': 4 };
      return (expOrder[a as keyof typeof expOrder] || 0) - (expOrder[b as keyof typeof expOrder] || 0);
    });
  }
  
  if (demographic === 'nivel_educativo') {
    return categories.sort((a, b) => {
      const eduOrder = { 'Escuela Superior': 1, 'Estudios Teológicos Técnicos': 2, 'Bachillerato': 3, 'Maestría': 4, 'Doctorado': 5 };
      return (eduOrder[a as keyof typeof eduOrder] || 0) - (eduOrder[b as keyof typeof eduOrder] || 0);
    });
  }
  
  if (demographic === 'sexo') {
    return categories.sort((a, b) => {
      const sexoOrder = { 'Masculino': 1, 'Femenino': 2 };
      return (sexoOrder[a as keyof typeof sexoOrder] || 0) - (sexoOrder[b as keyof typeof sexoOrder] || 0);
    });
  }
  
  return categories.sort();
};

// Function to get custom colors for sexo demographic
export const getSexoColors = (categories: string[]): string[] => {
  const sexoColorMap: Record<string, string> = {
    'Masculino': '#1F77B4', // Blue
    'Femenino': '#FF69B4'   // Pink
  };
  
  return categories.map(cat => sexoColorMap[cat] || '#7F7F7F');
};

export const createShortMapping = (): Record<string, string> => {
  return {
    q1_salud_mental_bienestar: '1. La salud mental es un componente importante del bienestar espiritual.',
    q2_pastores_identificar_trastornos: '2. Los pastores deberían estar preparados para identificar señales de trastornos mentales.',
    q3_salud_mental_taboo: '3. Hablar de salud mental en la iglesia es un tema tabú.',
    q4_adicciones_enfermos_no_pecadores: '4. Las personas con adicciones deben ser tratadas como enfermos y no como pecadores.',
    q5_preparado_consejeria_emocional: '5. Me siento preparado para brindar consejería básica a personas con problemas emocionales.',
    q6_fe_recuperacion_adicciones: '6. Considero que la fe tiene un papel importante en la recuperación de adicciones.',
    q7_iglesia_promueve_salud_mental: '7. En mi iglesia se promueve el cuidado de la salud mental.',
    q8_adicciones_apoyo_espiritual_clinico: '8. Las adicciones deben tratarse con apoyo espiritual y tratamiento clínico.',
    q9_capacitacion_salud_mental_adicciones: '9. Me gustaría recibir capacitación en temas de salud mental y adicciones.',
    q10_enfermedades_mentales_pecado: '10. Las enfermedades mentales son resultado exclusivo del pecado.',
    q11_conozco_profesionales_referir: '11. Conozco profesionales a los que puedo referir casos de salud mental.',
    q12_acompanado_personas_adiccion: '12. En mi ministerio he acompañado personas con problemas de adicción.',
    q13_iglesias_trabajar_psicologos: '13. Las iglesias deben trabajar con psicólogos y otros profesionales de salud mental.',
    q14_adicciones_enfermedad_intervencion_medica: '14. Las adicciones son una enfermedad que requiere intervención médica.',
    q15_hablar_depresion_pulpito: '15. Hablar de depresión desde el púlpito es importante.',
    q16_estigma_salud_mental_cristiano: '16. Existe estigma hacia la salud mental dentro del entorno cristiano.',
    q17_personas_problemas_mentales_acompanadas: '17. Las personas con problemas mentales deben ser acompañadas pastoralmente.',
    q18_iglesias_alianzas_centros_tratamiento: '18. Las iglesias deben formar alianzas con centros de tratamiento.',
    q19_oracion_suficiente_adiccion: '19. La oración es suficiente para tratar las adicciones.',
    q20_entiendo_diferencias_ansiedad_depresion: '20. Entiendo las diferencias entre ansiedad, depresión y trastornos psicóticos.',
    q21_he_predicado_salud_mental_pulpito: '21. He predicado desde el púlpito sobre temas relacionados con salud mental o adicciones.',
    q22_iglesias_rol_prevencion_suicidio: '22. Considero que las iglesias tienen un rol activo en la prevención del suicidio.',
    q23_personas_adicciones_integradas_iglesia: '23. Las personas que luchan con adicciones deben ser integradas plenamente en la vida de la iglesia.',
    q24_comodo_hablar_profesionales_salud_mental: '24. Me siento cómodo hablando con profesionales de salud mental sobre casos que veo en la iglesia.',
    q25_falta_conocimiento_limita_apoyo: '25. Considero que la falta de conocimiento sobre salud mental limita el apoyo pastoral efectivo.',
    q26_pastor_evaluacion_psicologica: '26. Debe tener el pastor una evaluación psicológica antes de comenzar a pastorear',
    q27_educacion_continua_salud_mental: '27. Debe ser requisito educación continua sobre la salud mental y adicciones en la pastoral'
  };
};


export const calculateStatistics = (data: SurveyData[], varX: string, varY: string): Statistics[] => {
  const maleData = data.filter(d => d.sexo === 'Masculino');
  const femaleData = data.filter(d => d.sexo === 'Femenino');

  const calculateStats = (groupData: SurveyData[]): Statistics => {
    const xValues = groupData.map(d => Number(d[varX as keyof SurveyData])).filter(val => !isNaN(val));
    const yValues = groupData.map(d => Number(d[varY as keyof SurveyData])).filter(val => !isNaN(val));

    if (xValues.length === 0 || yValues.length === 0) {
      return {
        genero: groupData[0]?.sexo || '',
        n: 0,
        mediaX: '0.00',
        mediaY: '0.00',
        sdX: '0.00',
        sdY: '0.00',
        correlacion: '0.00'
      };
    }

    const mediaX = xValues.reduce((sum, val) => sum + val, 0) / xValues.length;
    const mediaY = yValues.reduce((sum, val) => sum + val, 0) / yValues.length;

    const sdX = Math.sqrt(xValues.reduce((sum, val) => sum + Math.pow(val - mediaX, 2), 0) / xValues.length);
    const sdY = Math.sqrt(yValues.reduce((sum, val) => sum + Math.pow(val - mediaY, 2), 0) / yValues.length);

    const covariance = xValues.reduce((sum, val, i) => sum + (val - mediaX) * (yValues[i] - mediaY), 0) / xValues.length;
    const correlacion = sdX * sdY !== 0 ? covariance / (sdX * sdY) : 0;

    return {
      genero: groupData[0]?.sexo || '',
      n: xValues.length,
      mediaX: mediaX.toFixed(2),
      mediaY: mediaY.toFixed(2),
      sdX: sdX.toFixed(2),
      sdY: sdY.toFixed(2),
      correlacion: correlacion.toFixed(3)
    };
  };

  return [calculateStats(maleData), calculateStats(femaleData)];
};

export const calculateRegressionLine = (data: SurveyData[], varX: string, varY: string) => {
  const validData = data.filter(d => 
    d[varX as keyof SurveyData] !== null && 
    d[varY as keyof SurveyData] !== null &&
    !isNaN(Number(d[varX as keyof SurveyData])) &&
    !isNaN(Number(d[varY as keyof SurveyData]))
  );

  if (validData.length < 2) return [];

  const xValues = validData.map(d => Number(d[varX as keyof SurveyData]));
  const yValues = validData.map(d => Number(d[varY as keyof SurveyData]));

  const n = xValues.length;
  const sumX = xValues.reduce((sum, val) => sum + val, 0);
  const sumY = yValues.reduce((sum, val) => sum + val, 0);
  const sumXY = xValues.reduce((sum, val, i) => sum + val * yValues[i], 0);
  const sumX2 = xValues.reduce((sum, val) => sum + val * val, 0);

  const denominator = (n * sumX2 - sumX * sumX);
  
  // Check for division by zero or invalid denominator
  if (Math.abs(denominator) < 1e-10) {
    // If denominator is too small, return a horizontal line at the mean
    const meanY = sumY / n;
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    return [
      { x: minX, y: meanY },
      { x: maxX, y: meanY }
    ];
  }

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  // Check for invalid slope or intercept
  if (!isFinite(slope) || !isFinite(intercept)) {
    return [];
  }

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);

  const points = [];
  const step = Math.max((maxX - minX) / 50, 0.01); // Ensure minimum step size
  for (let x = minX; x <= maxX; x += step) {
    const y = slope * x + intercept;
    if (isFinite(y)) {
      points.push({ x, y });
    }
  }

  return points;
};

// Function to calculate correlation by demographic
export const calculateCorrelationByDemographic = (data: SurveyData[], demographic: string, question1: string, question2: string) => {
  const demographicCategories = Array.from(new Set(data.map(d => d[demographic as keyof SurveyData]).filter(val => val && String(val).trim() !== '')));
  
  // Sort categories using centralized sorting utility
  const sortedCategories = sortCategories(demographicCategories as string[], demographic);
  
  return sortedCategories.map(category => {
    const categoryData = data.filter(d => d[demographic as keyof SurveyData] === category);
    
    const xValues = categoryData.map(d => Number(d[question1 as keyof SurveyData])).filter(val => !isNaN(val));
    const yValues = categoryData.map(d => Number(d[question2 as keyof SurveyData])).filter(val => !isNaN(val));
    
    if (xValues.length < 2 || yValues.length < 2) {
      return {
        category: category as string,
        correlation: 0,
        valid: false,
        n: 0,
        avgX: 0,
        avgY: 0
      };
    }
    
    const n = xValues.length;
    const sumX = xValues.reduce((sum, val) => sum + val, 0);
    const sumY = yValues.reduce((sum, val) => sum + val, 0);
    const sumXY = xValues.reduce((sum, val, i) => sum + val * yValues[i], 0);
    const sumX2 = xValues.reduce((sum, val) => sum + val * val, 0);
    const sumY2 = yValues.reduce((sum, val) => sum + val * val, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    const correlation = denominator !== 0 ? numerator / denominator : 0;
    const avgX = sumX / n;
    const avgY = sumY / n;
    
    return {
      category: category as string,
      correlation: correlation,
      valid: true,
      n: n,
      avgX: avgX,
      avgY: avgY
    };
  });
};

// Function to create Cleveland Dot Plot data with 2 demographic variables
export const createClevelandDotPlotData = (data: SurveyData[], question: string, yAxisDemo: string, colorDemo: string) => {
  try {
    // Get unique categories for each demographic
    const yAxisCategories = Array.from(new Set(data.map(d => d[yAxisDemo as keyof SurveyData]).filter(val => val && String(val).trim() !== '')));
    const colorCategories = Array.from(new Set(data.map(d => d[colorDemo as keyof SurveyData]).filter(val => val && String(val).trim() !== '')));

    // Use centralized sorting utility

    const sortedYAxisCategories = sortCategories(yAxisCategories as string[], yAxisDemo);
    const sortedColorCategories = sortCategories(colorCategories as string[], colorDemo);

    const traces: any[] = [];

    // Define colors that match the pie chart
    const plotColors = [
      '#1F77B4', // navyBlue
      '#17BECF', // teal
      '#FF7F0E', // burntOrange
      '#7F7F7F', // slateGray
      '#2E8B57', // green
      '#8A2BE2', // purple
      '#FF6347', // red
      '#20B2AA', // cyan
      '#FF8C00', // orange
      '#9370DB', // violet
      '#3CB371', // sea green
      '#FF69B4'  // pink
    ];
    
    // Use custom colors for sexo demographic
    const colorsToUse = colorDemo === 'sexo' ? getSexoColors(sortedColorCategories) : plotColors;

    // Create traces for each color category
    sortedColorCategories.forEach((colorCat, colorIndex) => {
      const xValues: number[] = [];
      const yValues: string[] = [];
      const texts: string[] = [];

      // For each Y-axis category (maintain Y-axis order), calculate the average response
      sortedYAxisCategories.forEach(yAxisCat => {
        const filteredData = data.filter(d => {
          const hasYAxis = d[yAxisDemo as keyof SurveyData] === yAxisCat;
          const hasColor = d[colorDemo as keyof SurveyData] === colorCat;
          const hasValidQuestion = d[question as keyof SurveyData] !== null && 
                                 d[question as keyof SurveyData] !== undefined && 
                                 !isNaN(Number(d[question as keyof SurveyData])) &&
                                 Number(d[question as keyof SurveyData]) >= 1 &&
                                 Number(d[question as keyof SurveyData]) <= 5;
          
          return hasYAxis && hasColor && hasValidQuestion;
        });

        if (filteredData.length > 0) {
          const responses = filteredData.map(d => Number(d[question as keyof SurveyData]));
          const averageResponse = responses.reduce((sum, val) => sum + val, 0) / responses.length;
          
          xValues.push(averageResponse);
          yValues.push(yAxisCat);
          texts.push(`${yAxisCat}<br>${colorCat}<br>Promedio: ${averageResponse.toFixed(2)}<br>Muestras: ${filteredData.length}`);
        }
      });

      if (xValues.length > 0) {
        traces.push({
          x: xValues,
          y: yValues,
          mode: 'markers',
          type: 'scatter',
          name: colorCat,
          marker: {
            size: 12,
            color: colorsToUse[colorIndex % colorsToUse.length], // Use consistent colors
            opacity: 0.8,
            line: {
              width: 2,
              color: 'white'
            }
          },
          text: texts,
          hovertemplate: '<b>%{text}</b><extra></extra>',
          showlegend: true
        });
      }
    });

    // Sort traces by color category order (maintain logical order)
    traces.sort((a, b) => {
      const colorIndexA = sortedColorCategories.indexOf(a.name);
      const colorIndexB = sortedColorCategories.indexOf(b.name);
      return colorIndexA - colorIndexB;
    });

    return traces.length > 0 ? traces : null;
  } catch (error) {
    console.error('Error creating Cleveland dot plot data:', error);
    return null;
  }
};
